import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import AccountCircleRounded from '@mui/icons-material/AccountCircleRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import EventNoteRounded from '@mui/icons-material/EventNoteRounded';
import HikingRounded from '@mui/icons-material/HikingRounded';
import HomeRounded from '@mui/icons-material/HomeRounded';
import LoginRounded from '@mui/icons-material/LoginRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import MenuRounded from '@mui/icons-material/MenuRounded';
import MonitorHeartRounded from '@mui/icons-material/MonitorHeartRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import SettingsRounded from '@mui/icons-material/SettingsRounded';
import TrailSafeLogo from 'assets/TrailSafe_TempLogo.png';
import { ROUTES } from 'constants/RoutePaths';
import { signInWithRedirect, decodeJWT, signOut } from 'aws-amplify/auth';
import { userToken } from '_actions/user_actions';
import { setUserEmail, setUserId } from 'app/state';

import { useGetUserDetailsQuery } from 'app/api/usersApiSlice';
import useAuth from 'hooks/useAuth';
import './TSNavBar.module.scss';

export default function TSNavBar() {
  const dispatch = useDispatch();
  const { isAdmin, isSafetyMonitor } = useAuth();

  const userEmail = useSelector((state) => state.global?.userEmail);
  const [anchorEl1, setAnchorEl1] = React.useState(null);
  const [anchorEl2, setAnchorEl2] = React.useState(null);

  const [isAuth, setIsAuth] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const t = await userToken();
      const decodedToken = t ? decodeJWT(t.toString()).payload : null;
      setIsAuth(!!t);
      dispatch(setUserEmail(decodedToken?.email));
    };
    checkAuth();
    setIsLoading(false);
  }, []);

  const { data: userData, isLoading: isUserLoading } = useGetUserDetailsQuery(
    userEmail,
    { skip: !userEmail }
  );

  useEffect(() => {
    if (userData) {
      console.log('userData First: ', userData);
      dispatch(setUserId(userData?.[0]?.user_id));
    } else if (!isUserLoading) {
      console.log('No user data found for email: ', userEmail);
    }
  }, [userData]);

  const handleSignIn = () => {
    const path = location.pathname + location.search;
    localStorage.setItem('postSignInPath', path);
    signInWithRedirect();
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      console.log('User signed out successfully');
      setIsAuth(false);
      // You can add logic here to redirect the user or update UI state
      navigate('/');
    } catch (error) {
      console.log('error signing out: ', error);
    }
  };

  const handleNavigate = (route) => {
    setAnchorEl1(null);
    setAnchorEl2(null);
    navigate(route);
  };

  const handleAcctMenu = (event) => {
    setAnchorEl2(event.currentTarget);
  };

  const handleMenu = (event) => {
    setAnchorEl1(event.currentTarget);
  };

  const handleCloseAcctMenu = () => {
    setAnchorEl2(null);
  };
  const handleCloseMenu = () => {
    setAnchorEl1(null);
  };

  const name = userData?.[0]?.full_name;
  const mainLinks = [
    [ROUTES.HOME, 'Home', HomeRounded],
    [ROUTES.TRAIL_HOME, 'Find a trail', HikingRounded],
    ...(isAuth ? [[ROUTES.MY_PLANS, 'My Plans', EventNoteRounded]] : []),
    ...(isAdmin || isSafetyMonitor
      ? [[ROUTES.LIVE_ACTIVITIES, 'Live Activities', MonitorHeartRounded]]
      : []),
    ...(isAdmin
      ? [
          [
            ROUTES.ADMIN_WELFARE_CHECK_SETTINGS,
            'Welfare Check Settings',
            SettingsRounded,
          ],
        ]
      : []),
  ];
  const isCurrent = (route) => location.pathname === `/${route}`;

  if (isLoading || isUserLoading) {
    return <div>Loading...</div>;
  } else {
    return (
      <header className="ts-nav">
        <div className="ts-nav__bar">
          <IconButton
            className="ts-nav__icon-btn"
            aria-label="Open menu"
            aria-haspopup="true"
            onClick={(e) => handleMenu(e)}
          >
            <MenuRounded />
          </IconButton>
          <button
            type="button"
            className="ts-nav__brand"
            onClick={() => handleNavigate(`/${ROUTES.HOME}`)}
          >
            TrailSafe
          </button>
          {isAuth ? (
            <IconButton
              className="ts-nav__icon-btn"
              aria-label="Account menu"
              aria-controls="acct-menu-appbar"
              aria-haspopup="true"
              onClick={handleAcctMenu}
            >
              <AccountCircleRounded />
            </IconButton>
          ) : (
            <button
              type="button"
              className="ts-nav__sign-in"
              onClick={handleSignIn}
            >
              <LoginRounded aria-hidden="true" />
              Sign in
            </button>
          )}
        </div>

        <Drawer
          anchor="left"
          open={Boolean(anchorEl1)}
          onClose={handleCloseMenu}
          PaperProps={{ className: 'ts-drawer' }}
        >
          <div className="ts-drawer__head">
            <img src={TrailSafeLogo} alt="" className="ts-drawer__logo" />
            <span className="ts-drawer__brand">TrailSafe</span>
            <IconButton aria-label="Close menu" onClick={handleCloseMenu}>
              <CloseRounded />
            </IconButton>
          </div>
          <nav aria-label="Main">
            <ul className="ts-menu-list">
              {mainLinks.map(([route, label, Icon]) => (
                <li key={route}>
                  <button
                    type="button"
                    className={`ts-menu-item ${
                      isCurrent(route) ? 'ts-menu-item--current' : ''
                    }`}
                    aria-current={isCurrent(route) ? 'page' : undefined}
                    onClick={() => handleNavigate(`/${route}`)}
                  >
                    <Icon aria-hidden="true" />
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </Drawer>

        {isAuth && (
          <Menu
            id="acct-menu-appbar"
            anchorEl={anchorEl2}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(anchorEl2)}
            onClose={handleCloseAcctMenu}
            PaperProps={{ className: 'ts-account-menu' }}
            MenuListProps={{ className: 'ts-menu-list' }}
          >
            <li className="ts-account-menu__who" aria-hidden="true">
              <span className="ts-account-menu__name">
                {name || 'Signed in'}
              </span>
              {userEmail && (
                <span className="ts-account-menu__email">{userEmail}</span>
              )}
            </li>
            <MenuItem
              className="ts-menu-item"
              onClick={() => handleNavigate(`/${ROUTES.MY_PLANS}`)}
            >
              <EventNoteRounded aria-hidden="true" />
              My Plans
            </MenuItem>
            <MenuItem
              className="ts-menu-item"
              onClick={() => handleNavigate(`/${ROUTES.PROFILE}`)}
            >
              <PersonRounded aria-hidden="true" />
              Profile and contacts
            </MenuItem>
            <MenuItem
              className="ts-menu-item ts-menu-item--signout"
              onClick={handleSignOut}
            >
              <LogoutRounded aria-hidden="true" />
              Sign out
            </MenuItem>
          </Menu>
        )}
      </header>
    );
  }
}
